/**
 * Safely parse JSON from a Response object, handling cases where
 * Cloudflare/Heroku returns HTML error pages instead of JSON.
 *
 * When infrastructure returns HTML (e.g., "<!DOCTYPE html>..."),
 * throws RefreshPageError to show friendly error message to user.
 *
 * @param response - The Response object to parse
 * @returns Parsed JSON data
 * @throws Error with message 'RefreshPageError' if HTML is returned
 *
 * @example
 * const response = await fetch('/api/endpoint');
 * const data = await safeJsonParse(response);
 */
export async function safeJsonParse<T = unknown>(response: Response): Promise<T> {
  try {
    return await response.json();
  } catch (error) {
    // Response was HTML (e.g., Cloudflare error page) instead of JSON
    // Check for common HTML indicators in the SyntaxError message
    if (error instanceof SyntaxError) {
      const message = error.message.toLowerCase();
      if (message.includes('<!doctype') || message.includes('<html') || message.includes("unexpected token '<'")) {
        throw new Error('RefreshPageError');
      }
    }
    // Re-throw other errors (network issues, etc.)
    throw error;
  }
}
